import { useState, type FormEvent, type ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '@/app/auth/context';
import { PageHeader } from '@/components/admin/AdminShell';
import { FieldList, MetaBox } from '@/components/admin/MetaBox';
import { Notice } from '@/components/admin/Notice';
import {
  ROLE_LABELS,
  changePassword,
  deleteOwnProfilePhoto,
  updateOwnProfile,
  uploadOwnProfilePhoto,
  type CurrentUser,
} from '@/features/auth/api';
import { ProfileFields, toDraft, toInput } from '@/features/auth/ProfileFields';
import { MemberPhotoField } from '@/features/members/MemberPhotoField';
import { ApiError } from '@/lib/api/http';
import { formatDateTime } from '@/lib/datetime';

const MIN_PASSWORD_LENGTH = 10;

/**
 * 自分のプロフィール（顔写真・表示名・自己紹介）の編集（D-081）。
 * 受講生は `members` 側が正のためここには出さない。
 */
function OwnProfileBox({
  user,
  onUpdated,
}: {
  user: CurrentUser;
  onUpdated: (updated: CurrentUser) => void;
}): ReactElement {
  const [draft, setDraft] = useState(() => toDraft(user));
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      onUpdated(await updateOwnProfile(toInput(draft)));
      setNotice('プロフィールを保存しました。');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'プロフィールを保存できませんでした。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mb-4">
      <MetaBox title="プロフィール">
        {notice && (
          <Notice kind="success" onDismiss={() => setNotice(null)}>
            {notice}
          </Notice>
        )}
        {error && (
          <Notice kind="error" dismissible={false}>
            {error}
          </Notice>
        )}

        <MemberPhotoField
          photoUrl={user.photo_url}
          name={user.display_name ?? user.email}
          editable
          onUpload={async (file) => onUpdated(await uploadOwnProfilePhoto(file))}
          onDelete={async () => onUpdated(await deleteOwnProfilePhoto())}
        />

        <form className="mt-4" onSubmit={(event) => void onSubmit(event)}>
          <ProfileFields draft={draft} onChange={setDraft} idPrefix="me" />

          <button
            type="submit"
            disabled={busy}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
          >
            {busy ? '保存中…' : 'プロフィールを保存する'}
          </button>
        </form>
      </MetaBox>
    </div>
  );
}

/**
 * アカウント設定（指示書 §10 共通 / §12 M12）。
 * アカウント情報の表示と、パスワード変更（パスワードでログインするロールのみ）。
 * 2要素認証のリセットはスーパー管理者経由（D-046）。
 */
export function AccountPage(): ReactElement {
  const { user, setUser } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!user) return <></>;

  // 受講生のプロフィールは会員側（マイプロフィール）が正。ここでは編集させない（D-081）
  const isStudent = user.active_role === 'student';

  const totpStatus = !user.totp_required
    ? '対象外（受講生・理事講師・サクセスは不要）'
    : user.totp_enrolled
      ? '設定済み'
      : '未設定（次回ログイン時に設定）';

  // 事務局・スーパー管理者は Google ログインが基本だが、固定IPからの緊急ログイン用に
  // パスワードを設定している場合は変更欄を出す（D-068）
  const usesPassword = !user.totp_required || user.has_password;

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setNotice(null);

    if (newPassword !== confirmPassword) {
      setError('新しいパスワードが一致しません。');
      return;
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(`新しいパスワードは${MIN_PASSWORD_LENGTH}文字以上にしてください。`);
      return;
    }

    setBusy(true);
    try {
      await changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setNotice(
        'パスワードを変更しました。他の端末でログインしていた場合は、ログインし直しが必要です。',
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'パスワードを変更できませんでした。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader title="アカウント設定" />

      {notice && (
        <Notice kind="success" onDismiss={() => setNotice(null)}>
          {notice}
        </Notice>
      )}
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}

      {isStudent ? (
        <div className="mb-4">
          <MetaBox title="プロフィール">
            <p className="text-wptext-muted">
              顔写真・自己紹介・連絡先は{' '}
              <Link to="/profile" className="text-wpaccent hover:underline">
                マイプロフィール
              </Link>{' '}
              から編集してください。
            </p>
          </MetaBox>
        </div>
      ) : (
        // key を付けて、ログイン情報が読み込まれてから初期値を入れる
        // （読み込み前の空文字で固定されるのを避ける）
        <OwnProfileBox key={user.id} user={user} onUpdated={setUser} />
      )}

      <MetaBox title="アカウント情報">
        <FieldList
          fields={[
            { label: 'メールアドレス', value: user.email },
            { label: '表示名', value: user.display_name ?? '—' },
            {
              label: '保有ロール',
              value: user.roles.map((r) => ROLE_LABELS[r.role]).join(' / '),
            },
            { label: '現在のロール', value: ROLE_LABELS[user.active_role] },
            { label: '2要素認証', value: totpStatus },
            {
              label: '最終ログイン',
              value: user.last_login_at
                ? formatDateTime(user.last_login_at)
                : '—',
            },
          ]}
        />
        {user.totp_required && user.totp_enrolled && (
          <p className="mt-3 text-wptext-muted">
            2要素認証の再設定（端末紛失時など）はスーパー管理者に依頼してください。
          </p>
        )}
      </MetaBox>

      {usesPassword && (
        <div className="mt-4">
          <MetaBox title="パスワードの変更">
            <form onSubmit={(event) => void onSubmit(event)}>
              <label htmlFor="current-password" className="mb-1.5 block text-wptext-muted">
                現在のパスワード
              </label>
              <input
                id="current-password"
                type="password"
                autoComplete="current-password"
                required
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                className="mb-4 w-full max-w-sm rounded-sm border border-wpborder px-3 py-2"
              />

              <label htmlFor="new-password" className="mb-1.5 block text-wptext-muted">
                新しいパスワード（{MIN_PASSWORD_LENGTH}文字以上）
              </label>
              <input
                id="new-password"
                type="password"
                autoComplete="new-password"
                required
                minLength={MIN_PASSWORD_LENGTH}
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                className="mb-4 w-full max-w-sm rounded-sm border border-wpborder px-3 py-2"
              />

              <label htmlFor="confirm-password" className="mb-1.5 block text-wptext-muted">
                新しいパスワード（確認のためもう一度）
              </label>
              <input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                className="mb-4 w-full max-w-sm rounded-sm border border-wpborder px-3 py-2"
              />

              <button
                type="submit"
                disabled={busy}
                className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
              >
                {busy ? '変更中…' : 'パスワードを変更する'}
              </button>
              <p className="mt-2 text-xs text-wptext-muted">
                変更すると、他の端末でログインしたままの状態は解除されます（この端末はそのまま使えます）。
              </p>
            </form>
          </MetaBox>
        </div>
      )}
    </>
  );
}
